import { Component, OnInit } from '@angular/core'
import { LoginService } from 'src/app/login/login.service'
import { IosBuildAutomationService } from './ios-build-automation.service'
import { AutomationOptions, BuildSummary, CombinedBuildSummary, IosAutomationOptions, IosBuildSummary } from './automatic-build.types'
import { AndroidBuildAutomationService } from './android-build-automation.service'

@Component({
  selector: 'app-automatic-build',
  templateUrl: './automatic-build.component.html',
  styleUrl: './automatic-build.component.scss'
})
export class AutomaticBuildComponent {
  statusMsg = ''
  errorMsg = ''

  // Auto-run automation properties
  autoRunning = false
  autoRunSummary: CombinedBuildSummary = { android: null, ios: null }
  autoRunBuildType = 'aab'
  autoRunWhatsNew = ''
  autoRunAutoUpload = true

  // Platform selection
  buildAndroid = true
  buildIos = true

  // Current platform being built
  currentPlatform: 'android' | 'ios' | null = null

  constructor(
    public androidAutomationService: AndroidBuildAutomationService,
    public iosAutomationService: IosBuildAutomationService,
    public loginService: LoginService
  ) {}

  ngOnInit(): void {}

  /**
   * Starts the automated build process for all purchase projects (Android and/or iOS)
   */
  startAutoRun(): void {
    if (this.autoRunning || this.androidAutomationService.isRunning || this.iosAutomationService.isRunning) {
      return
    }

    if (!this.buildAndroid && !this.buildIos) {
      this.errorMsg = 'Please select at least one platform to build.'
      return
    }

    const currentUser = this.loginService.getCurrentUser()
    if (!currentUser?.admin) {
      this.errorMsg = 'Admin access required to run automated builds.'
      return
    }

    const email = currentUser.email
    if (!email) {
      this.errorMsg = 'No user email found. Please log in again.'
      return
    }

    this.autoRunning = true
    this.autoRunSummary = { android: null, ios: null }
    this.errorMsg = ''

    if (this.buildAndroid) {
      this.runAndroidBuilds(email)
    } else if (this.buildIos) {
      this.runIosBuilds(email)
    }
  }

  /**
   * Runs Android builds for all projects
   */
  private runAndroidBuilds(email: string): void {
    this.currentPlatform = 'android'
    this.statusMsg = 'Starting automated Android builds for purchase projects...'

    const options: AutomationOptions = {
      buildType: this.autoRunBuildType,
      whatsNew: this.autoRunWhatsNew || 'Bug fixes and performance improvements',
      autoUpload: this.autoRunAutoUpload
    }

    this.androidAutomationService.runAutomatedBuilds(email, options).subscribe({
      next: (summary: BuildSummary) => {
        this.autoRunSummary.android = summary
        this.statusMsg = `Android complete: ${summary.completed.length} built, ${summary.failed.length} failed, ${summary.skipped.length} skipped`

        // If iOS is also selected, run iOS builds next
        if (this.buildIos) {
          this.runIosBuilds(email)
        } else {
          this.finishAutoRun()
        }
      },
      error: (err) => {
        this.errorMsg = 'Android automation failed: ' + (err?.message || 'Unknown error')
        console.error('Android auto-run error:', err)

        // Continue with iOS if selected
        if (this.buildIos) {
          this.runIosBuilds(email)
        } else {
          this.finishAutoRun()
        }
      }
    })
  }

  /**
   * Runs iOS builds for all projects
   */
  private runIosBuilds(email: string): void {
    this.currentPlatform = 'ios'
    this.statusMsg = 'Starting automated iOS builds for purchase projects...'

    const options: IosAutomationOptions = {
      whatsNew: this.autoRunWhatsNew || 'Bug fixes and performance improvements'
    }

    this.iosAutomationService.runAutomatedBuilds(email, options).subscribe({
      next: (summary: IosBuildSummary) => {
        this.autoRunSummary.ios = summary
        this.statusMsg = `iOS complete: ${summary.completed.length} built, ${summary.failed.length} failed, ${summary.skipped.length} skipped`
        this.finishAutoRun()
      },
      error: (err) => {
        this.errorMsg = 'iOS automation failed: ' + (err?.message || 'Unknown error')
        console.error('iOS auto-run error:', err)
        this.finishAutoRun()
      }
    })
  }

  /**
   * Finishes the auto-run process and updates the status
   */
  private finishAutoRun(): void {
    this.autoRunning = false
    this.currentPlatform = null

    const androidStats = this.autoRunSummary.android
    const iosStats = this.autoRunSummary.ios

    let statusParts: string[] = []

    if (androidStats) {
      statusParts.push(`Android: ${androidStats.completed.length} built, ${androidStats.failed.length} failed, ${androidStats.skipped.length} skipped`)
    }

    if (iosStats) {
      statusParts.push(`iOS: ${iosStats.completed.length} built, ${iosStats.failed.length} failed, ${iosStats.skipped.length} skipped`)
    }

    this.statusMsg = 'Automation complete - ' + statusParts.join(' | ')
  }

  /**
   * Cancels the ongoing automated build process
   */
  cancelAutoRun(): void {
    if (!this.autoRunning) {
      return
    }

    if (this.androidAutomationService.isRunning) {
      this.androidAutomationService.cancel()
    }

    if (this.iosAutomationService.isRunning) {
      this.iosAutomationService.cancel()
    }

    this.statusMsg = 'Cancelling automation... (waiting for current build to finish)'
  }

  /**
   * Gets the current automation progress for display (Android)
   */
  get androidProgress() {
    return this.androidAutomationService.progress
  }

  /**
   * Gets the current automation progress for display (iOS)
   */
  get iosProgress() {
    return this.iosAutomationService.progress
  }

  /**
   * Gets the current active progress based on platform
   */
  get currentProgress() {
    if (this.currentPlatform === 'android') {
      return this.androidAutomationService.progress
    } else if (this.currentPlatform === 'ios') {
      return this.iosAutomationService.progress
    }
    return null
  }

  /**
   * Total completed builds across both platforms
   */
  get totalCompleted(): number {
    return (this.autoRunSummary.android?.completed.length || 0) + (this.autoRunSummary.ios?.completed.length || 0)
  }

  /**
   * Total failed builds across both platforms
   */
  get totalFailed(): number {
    return (this.autoRunSummary.android?.failed.length || 0) + (this.autoRunSummary.ios?.failed.length || 0)
  }

  /**
   * Total skipped builds across both platforms
   */
  get totalSkipped(): number {
    return (this.autoRunSummary.android?.skipped.length || 0) + (this.autoRunSummary.ios?.skipped.length || 0)
  }
}
